import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Divider,
  Button,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
  ArrowBack,
  Share,
  Print,
  AccessTime,
  Visibility,
  ArrowForward,
  Home,
} from '@mui/icons-material';

import { getUserDetails } from '../utils';
import {
  useQueryGetHelpArticle,
  useMutationMarkArticleHelpful,
} from '../hooks/react-query/helpArticles';

function HelpArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const userDetails = getUserDetails();
  const userToken = userDetails?.token;

  const [hasMarkedHelpful, setHasMarkedHelpful] = useState(false);

  // Fetch article
  const { data: articleResponse, isLoading } = useQueryGetHelpArticle(slug, userToken, navigate, toast);

  // Mark as helpful mutation
  const { mutate: markHelpful, isLoading: markingHelpful } = useMutationMarkArticleHelpful(
    userToken,
    toast,
    navigate
  );

  const article = articleResponse?.data?.data;

  const handleMarkHelpful = () => {
    if (!hasMarkedHelpful) {
      markHelpful(article._id);
      setHasMarkedHelpful(true);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: article?.title,
          text: article?.excerpt,
          url: window.location.href,
        })
        .catch(() => {
          // User cancelled or error occurred
        });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRelatedArticleClick = (articleSlug) => {
    navigate(`/help/${articleSlug}`);
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#A94B9C' }} />
      </Box>
    );
  }

  if (!article) {
    return (
      <Container 
        maxWidth={false}
        sx={{ 
          py: 8, 
          px: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center' 
        }}
      >
        <Typography variant="h4" color="text.secondary" sx={{ mb: 2 }}>
          Article not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/help')}
          sx={{
            backgroundColor: '#A94B9C',
            '&:hover': { backgroundColor: '#8E3A7E' },
          }}
        >
          Back to Help Center
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | Help Center | Edifying Teachers</title>
        <meta name="description" content={article.excerpt} />
      </Helmet>

      <Container 
        maxWidth={false} 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        {/* Breadcrumbs - Consistent with Help Center */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={<Home sx={{ fontSize: 18 }} />}
            label="Help Center"
            onClick={() => navigate('/help')}
            variant="outlined"
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              borderColor: '#A94B9C',
              color: '#A94B9C',
              '&:hover': {
                backgroundColor: alpha('#A94B9C', 0.1),
              },
            }}
          />
          <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Chip
            label={article.category.replace(/-/g, ' ')}
            onClick={() => navigate(`/help?category=${article.category}`)}
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              backgroundColor: alpha('#A94B9C', 0.15),
              color: '#A94B9C',
              textTransform: 'capitalize',
              '&:hover': {
                backgroundColor: alpha('#A94B9C', 0.25),
              },
            }}
          />
          <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Chip
            label={article.title}
            sx={{
              backgroundColor: '#A94B9C',
              color: 'white',
              fontWeight: 600,
              maxWidth: { xs: '100%', sm: '400px' },
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </Stack>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                {/* Article Header */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: '#A94B9C',
                      mb: 2,
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                      lineHeight: 1.3,
                    }}
                  >
                    {article.title}
                  </Typography>

                  {/* Meta Info */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Updated {new Date(article.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Visibility sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {article.views} views
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {article.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: alpha('#A94B9C', 0.1),
                            color: '#A94B9C',
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* Article Content */}
                <Box
                  sx={{
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: '#A94B9C',
                      fontWeight: 700,
                      mt: 3,
                      mb: 2,
                    },
                    '& p': {
                      lineHeight: 1.8,
                      mb: 2,
                      color: 'text.primary',
                      fontSize: '1rem',
                    },
                    '& ul, & ol': {
                      pl: 3,
                      mb: 2,
                      '& li': {
                        mb: 1,
                        lineHeight: 1.8,
                      },
                    },
                    '& a': {
                      color: '#A94B9C',
                      textDecoration: 'underline',
                      '&:hover': {
                        color: '#8E3A7E',
                      },
                    },
                    '& code': {
                      backgroundColor: alpha('#A94B9C', 0.1),
                      padding: '2px 6px',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.9em',
                    },
                    '& pre': {
                      backgroundColor: '#f5f5f5',
                      padding: 2,
                      borderRadius: 2,
                      overflow: 'auto',
                      mb: 2,
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      my: 2,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <Divider sx={{ my: 4 }} />

                {/* Feedback Section */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Was this article helpful?
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant={hasMarkedHelpful ? 'contained' : 'outlined'}
                      startIcon={hasMarkedHelpful ? <ThumbUp /> : <ThumbUpOutlined />}
                      onClick={handleMarkHelpful}
                      disabled={markingHelpful || hasMarkedHelpful}
                      sx={{
                        ...(hasMarkedHelpful && {
                          backgroundColor: '#A94B9C',
                          color: 'white',
                          '&:hover': { backgroundColor: '#8E3A7E' },
                        }),
                        ...(!hasMarkedHelpful && {
                          borderColor: '#A94B9C',
                          color: '#A94B9C',
                          '&:hover': {
                            backgroundColor: alpha('#A94B9C', 0.1),
                            borderColor: '#8E3A7E',
                          },
                        }),
                      }}
                    >
                      {hasMarkedHelpful ? 'Marked as Helpful' : 'Yes, this helped!'}
                    </Button>
                  </Stack>
                  {article.helpfulCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      {article.helpfulCount} {article.helpfulCount === 1 ? 'person' : 'people'} found this
                      helpful
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Actions */}
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Actions
                  </Typography>
                  <Stack spacing={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Share />}
                      onClick={handleShare}
                      sx={{
                        borderColor: '#A94B9C',
                        color: '#A94B9C',
                        '&:hover': {
                          backgroundColor: alpha('#A94B9C', 0.1),
                          borderColor: '#8E3A7E',
                        },
                      }}
                    >
                      Share Article
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Print />}
                      onClick={handlePrint}
                      sx={{
                        borderColor: '#A94B9C',
                        color: '#A94B9C',
                        '&:hover': {
                          backgroundColor: alpha('#A94B9C', 0.1),
                          borderColor: '#8E3A7E',
                        },
                      }}
                    >
                      Print Article
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Related Articles */}
              {article.relatedArticles && article.relatedArticles.length > 0 && (
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Related Articles
                    </Typography>
                    <List disablePadding>
                      {article.relatedArticles.map((relatedArticle, index) => (
                        <React.Fragment key={relatedArticle._id}>
                          {index > 0 && <Divider sx={{ my: 1 }} />}
                          <ListItemButton
                            onClick={() => handleRelatedArticleClick(relatedArticle.slug)}
                            sx={{
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: alpha('#A94B9C', 0.05),
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {relatedArticle.title}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  {relatedArticle.excerpt?.substring(0, 80)}...
                                </Typography>
                              }
                            />
                            <ArrowForward sx={{ fontSize: 18, color: '#A94B9C', ml: 1 }} />
                          </ListItemButton>
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Contact Support */}
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Still need help?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Can't find what you're looking for? Our support team is here to help.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/dashboard/my-profile')}
                    sx={{
                      backgroundColor: 'white',
                      color: '#764ba2',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  >
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default HelpArticleDetail;
